/**
 * Schreibweisen zusammenbringen, bevor sie auseinanderlaufen.
 *
 * ═══ DAS PROBLEM, GEMESSEN ═══
 *
 * In der SharePoint-Liste standen 214 Schreibweisen für 128 Orte. „Werk
 * Herrschaftsholz", „werk herrschaftsholz", „Herrschaftsholz", „Hersch.holz",
 * „Werk Herschaftsholz" — für den Menschen dasselbe, für die Datenbank fünf
 * Orte, und für jede Auswertung fünf Zeilen statt einer.
 *
 * ═══ ZWEI HEBEL, BEIDE HIER ═══
 *
 * 1. SUCHEN, NICHT TIPPEN. Wer aus einer Liste wählt, erfindet keine neue
 *    Schreibweise. Die Suche muss deshalb großzügig sein: Groß- und
 *    Kleinschreibung, Umlaute, Bindestriche und Tippfehler dürfen nicht
 *    zwischen dem Fahrer und dem vorhandenen Eintrag stehen.
 *
 * 2. VOR DEM ANLEGEN FRAGEN. Findet die Suche nichts, wird nicht sofort ein
 *    neuer Eintrag angeboten, sondern zuerst: „Meinst du eventuell …". Das ist
 *    der Moment, in dem eine Dublette entsteht oder eben nicht — hinterher
 *    kostet sie Büroarbeit.
 *
 * Perfekt wird das nie. Es muss auch nicht: Was durchrutscht, führt das Büro
 * später zusammen (`ort.zusammengefuehrt_in`, `alt_schreibweisen`). Hier geht
 * es darum, die Menge dessen klein zu halten, was zusammengeführt werden muss.
 */

/** Alles weg, was zwei Schreibweisen desselben Wortes unterscheidet. */
export function normalisiert(text: string): string {
  return text
    .toLocaleLowerCase('de-DE')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Levenshtein — wie viele Zeichen müsste man ändern. */
function abstand(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let zeile = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let vorher = zeile[0];
    zeile[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const merk = zeile[j];
      zeile[j] = Math.min(
        zeile[j] + 1, // löschen
        zeile[j - 1] + 1, // einfügen
        vorher + (a[i - 1] === b[j - 1] ? 0 : 1) // ersetzen
      );
      vorher = merk;
    }
  }
  return zeile[b.length];
}

/** 1 = gleich, 0 = nichts gemeinsam. */
export function aehnlichkeit(a: string, b: string): number {
  const x = normalisiert(a);
  const y = normalisiert(b);
  if (!x || !y) return 0;
  if (x === y) return 1;
  return 1 - abstand(x, y) / Math.max(x.length, y.length);
}

/**
 * Passt dieser Eintrag zur Sucheingabe?
 *
 * Erst der einfache Fall (kommt vor), dann der wortweise (der Fahrer tippt
 * „holz", der Eintrag heißt „Werk Herrschaftsholz"), zuletzt der
 * fehlertolerante — aber erst ab drei Zeichen, sonst passt bei „ho" die halbe
 * Liste und die Suche wird unbrauchbar.
 */
export function passt(kandidat: string, suche: string): boolean {
  const s = normalisiert(suche);
  if (!s) return true;
  const k = normalisiert(kandidat);

  if (k.includes(s)) return true;
  if (s.split(' ').every((wort) => k.includes(wort))) return true;
  if (s.length < 3) return false;

  return k.split(' ').some((wort) => {
    if (aehnlichkeit(wort, s) >= 0.7) return true;

    // Der Fall, an dem die reine Wortaehnlichkeit scheitert: ein Tippfehler
    // MITTEN im Anfang eines langen Wortes. „hersch" gegen
    // „herrschaftsholz" ergibt 0,4 — nicht weil es unaehnlich waere, sondern
    // weil die Laenge den Wert bestimmt. Verglichen wird deshalb zusaetzlich
    // nur der Wortanfang in der Laenge der Eingabe (plus eins, damit ein
    // fehlender Buchstabe noch Platz hat): „herrsch" gegen „hersch" ergibt
    // 0,86 und trifft.
    //
    // Ohne das findet die Suche nach „hersch" ausgerechnet die falsche
    // Schreibweise und nicht die richtige — und jemand legt den Ort ein
    // drittes Mal an.
    const anfang = wort.slice(0, s.length + 1);
    return anfang.length >= 3 && aehnlichkeit(anfang, s) >= 0.7;
  });
}

/**
 * Wer könnte gemeint sein? Für die Rückfrage vor dem Neuanlegen.
 * Bewusst streng (0,62), damit die Frage selten kommt — und wenn, dann sitzt.
 */
export function aehnlicheTreffer<T extends { name: string }>(
  liste: T[],
  eingabe: string,
  hoechstens = 3
): T[] {
  const bewertet = liste
    .map((x) => ({ x, wert: aehnlichkeit(x.name, eingabe) }))
    .filter((b) => b.wert >= 0.62)
    .sort((a, b) => b.wert - a.wert);
  return bewertet.slice(0, hoechstens).map((b) => b.x);
}
